import {
  CatalogObject,
  MessageCollection,
  OpenQuestion,
  PATH_SEPARATOR,
  Row,
  Section,
  SuggestionsQuestion,
  TextMessage,
  Translation,
} from "anketa-core";
import { locationForm } from "./location";
import { opsanioNetworks } from "./network";
import { opsanioScanners } from "./scanners";
import { commentSection } from "./notes/section";
import { retentionSection } from "./retentions";
import {
  AnketaActions,
  AnketaActionsEnum,
  AnketaAuthUser,
  AnketaCheckPermissionOptions,
  AnketaResources,
  AnketaResourcesEnum,
  getOrgAttributeFlags,
  getYesNoAsBoolean,
  Vpc,
} from "../../../";
import { opsanioMostToken, vaultSettings } from "./token/ui";
import { HardwareForm } from "./hardware";
import { OPSANIO_WAVES } from "src/opsanio/waves/consts";
import {
  OPSANIO_SERVICE_MODELS,
  OPSANIO_SIZES,
  OpsanioCreateContactDefaults,
  OpsanioServiceModel,
  OpsanioSizing,
  OpsanioWaves,
} from "src/questionnaires";
import { opsanioAuth } from "./auth";
import { FeaturesForm } from "./features";
import { hardeningSection } from "./hardening";
import { aiSection } from "./ai";
import { generalInformationForm } from "./general";
import { salesForm } from "./sales";

export type QuestionsRightsTypes =
  | OpenQuestion
  | Section
  | SuggestionsQuestion
  | Row;

export class ContactOptionMap extends Map<string, TextMessage> {
  static readonly contact = "default.contact";
}
export class WaveOptionMap extends Map<OpsanioWaves, TextMessage> {
  static readonly wave = "default.wave";
}
export class SizingOptionMap extends Map<OpsanioSizing, TextMessage> {
  static readonly size = "default.sizing";
}
export class ServiceModelMap extends Map<OpsanioServiceModel, TextMessage> {
  static readonly service = "default.service.model";
}

export class OpsanioCatalog extends CatalogObject {
  private static _instance: OpsanioCatalog | undefined;

  public static get instance(): OpsanioCatalog {
    if (!OpsanioCatalog._instance) {
      throw new Error(
        "OpsanioCatalog instance has not been created. Use createInstance(name) method first.",
      );
    }
    return OpsanioCatalog._instance;
  }

  /**
   * Drops the cached catalog instance. Opening a questionnaire must start from
   * a clean slate: the previous instance is keyed by its own questionnaire id
   * and carries that questionnaire's facts, which the id-keyed browser-storage
   * cache (`anketa-<id>-facts`) would otherwise replay into the wrong form.
   */
  public static clearInstance(): void {
    OpsanioCatalog._instance = undefined;
  }

  public static createInstance(
    id: string,
    name: string,
    users: Array<string>,
    checkPermission: (
      resource: AnketaResources,
      action: AnketaActions,
    ) => boolean,
    oldValues: any,
    token: string,
    user: AnketaAuthUser,
    setDefaults?: boolean,
    defaultValues?: {
      agents: {
        id: string;
        macs: string[];
        redfishMac: string;
        serialNumber: string;
      }[];
      vpc: { vpcs: Vpc[]; tenantIds: number[] };
      vpcName: string;
      nextWave: OpsanioWaves;
      contacts?: OpsanioCreateContactDefaults;
    },
  ): OpsanioCatalog {
    // Always build a fresh catalog. Reusing a cached instance from a previously
    // opened questionnaire kept its id and facts, which then leaked that
    // questionnaire's answers into the one being opened (directly, and via the
    // id-keyed `anketa-<id>-facts` browser cache loaded by AnketaEuiContext).
    OpsanioCatalog._instance = new OpsanioCatalog(
      id,
      name,
      users,
      checkPermission,
      oldValues,
      token,
      user,
      setDefaults,
      defaultValues,
    );

    return OpsanioCatalog._instance;
  }

  constructor(
    id: string,
    name: string,
    users: Array<string>,
    checkPermission: (
      resource: AnketaResources,
      action: AnketaActions,
      options?: AnketaCheckPermissionOptions<AnketaResourcesEnum.questionnaires>,
    ) => boolean,
    oldValues: any,
    token: string,
    user: AnketaAuthUser,
    setDefaults?: boolean,
    defaultValues?: {
      agents: {
        id: string;
        macs: string[];
        redfishMac: string;
        serialNumber: string;
      }[];
      vpc: { vpcs: Vpc[]; tenantIds: number[] };
      vpcName: string;
      nextWave: OpsanioWaves;
      contacts?: OpsanioCreateContactDefaults;
    },
  ) {
    super(id, name);
    users.forEach((user) => {
      const path = encodeURIComponent(user.replaceAll(".", "_"));
      MessageCollection.Global.setTranslations(
        ContactOptionMap.contact + PATH_SEPARATOR + path,
        [new Translation("en", user), new Translation("de", user)],
      );
    });
    OPSANIO_WAVES.map((wave) => {
      MessageCollection.Global.setTranslations(
        WaveOptionMap.wave + PATH_SEPARATOR + wave,
        [new Translation("en", wave), new Translation("de", wave)],
      );
    });
    OPSANIO_SIZES.map((size) => {
      MessageCollection.Global.setTranslations(
        SizingOptionMap.size + PATH_SEPARATOR + size,
        [new Translation("en", size), new Translation("de", size)],
      );
    });
    OPSANIO_SERVICE_MODELS.map((service: OpsanioServiceModel) => {
      MessageCollection.Global.setTranslations(
        ServiceModelMap.service + PATH_SEPARATOR + service,
        [new Translation("en", service), new Translation("de", service)],
      );
    });

    const questionsRights = (
      q: QuestionsRightsTypes,
      options?: AnketaCheckPermissionOptions<AnketaResourcesEnum.questionnaires>,
    ) => {
      if (options !== undefined && options.hideForExternal !== undefined) {
        q.visible =
          options.hideForExternal &&
          getOrgAttributeFlags(user).isInternal &&
          user.roles.some((role) => role === "anketa-admin");
      } else {
        q.visible =
          options?.hideForExternal !== undefined
            ? !options.hideForExternal
            : checkPermission(
                AnketaResourcesEnum.questionnaires,
                AnketaActionsEnum.view,
              );
      }
      if ("readonly" in q && !q.readonly) {
        q.readonly = !checkPermission(
          AnketaResourcesEnum.questionnaires,
          AnketaActionsEnum.update,
          options,
        );
      }
    };

    const bgpRaw = oldValues ? oldValues["features.enable.bgp"] : false;
    let bgpEnabled: boolean =
      bgpRaw && bgpRaw != null ? getYesNoAsBoolean(bgpRaw.value) : false;

    // Whether the AI stack is on decides whether the AI section (spend budgets,
    // cost alerting) is shown at all. Read from the stored answer on open, then
    // kept current by the ai_ml change listener in FeaturesForm.
    const aiRaw = oldValues ? oldValues["features.enable.ai_ml"] : false;
    let aiEnabled: boolean =
      aiRaw && aiRaw != null ? (getYesNoAsBoolean(aiRaw.value) ?? false) : false;

    // Only the keys actually present are applied: the BGP and AI listeners each
    // report their own answer, and treating an absent key as false would let one
    // listener silently clear the other's state.
    const setFeaturesStatus = (values: { bgp?: boolean; ai?: boolean }) => {
      if (values.bgp !== undefined) bgpEnabled = values.bgp;
      if (values.ai !== undefined) {
        aiEnabled = values.ai;
        syncAiSection();
      }
    };

    // Declared before FeaturesForm runs (which is what installs the listener),
    // assigned once the section exists. A no-op until then, so the listener
    // firing during construction cannot reach an undefined section.
    let syncAiSection: () => void = () => {};

    const setDefaultValues = setDefaults !== undefined ? setDefaults : false;
    const defaultValuesFallback =
      defaultValues !== undefined
        ? defaultValues
        : {
            agents: [],
            vpc: { vpcs: [], tenantIds: [] },
            vpcName: "",
            redfishMac: "",
            serialNumber: "",
            nextWave: "wave1" as OpsanioWaves,
          };
    const authTabInternal = vaultSettings(questionsRights);
    this.addChild(authTabInternal);
    authTabInternal.addChild(
      opsanioMostToken(id, oldValues, questionsRights, token),
    );
    // Contacts are only seeded from wizard defaults at creation time
    // (setDefaultValues). On normal open (setDefaults false), pass undefined so
    // existing answers are never overwritten.
    const generalInformation = generalInformationForm(
      questionsRights,
      setDefaultValues ? defaultValues?.contacts : undefined,
    );
    this.addChild(generalInformation);

    const features = FeaturesForm(setDefaultValues, setFeaturesStatus);
    this.addChild(features);
    questionsRights(features, {
      isInternal: true,
      isExternal: false,
      hideForExternal: true,
    });

    // Spend budgets for the AI gateway, conditional on the AI/ML feature - the
    // same add/remove-on-change pattern the OIDC section uses. The section is
    // built once and attached or detached, rather than rebuilt, so answers
    // already typed survive toggling AI off and back on within one session.
    const aiBudgets = aiSection(questionsRights, setDefaultValues);
    syncAiSection = () => {
      if (aiEnabled && !this.containsChild(aiBudgets)) {
        this.addChild(aiBudgets);
      } else if (!aiEnabled && this.containsChild(aiBudgets)) {
        this.removeChild(aiBudgets);
      }
    };
    syncAiSection();

    const assemblySite = locationForm(questionsRights, setDefaultValues);
    this.addChild(assemblySite);

    // "Do you want a two-room solution?" (cpe.tworoom.tworoomq) is answered in
    // the location section but decides what the network section shows: whether
    // the two rooms can have different customer networks, and whether each
    // agent gets a room to be assigned to.
    //
    // The QUESTION OBJECT is handed over, not its current value. Passing a
    // boolean read from oldValues would only take effect after a save and a
    // reopen - the network section would sit there unchanged while the answer
    // above it said Yes, which is exactly how this was first reported. With the
    // object, the network section subscribes to it and updates as it is
    // toggled.
    //
    // Located by walking the section rather than changing locationForm's return
    // type, which is used elsewhere; undefined if it is ever renamed or moved,
    // and the network section then simply behaves as a single-room site.
    const findQuestion = (item: any, id: string): any => {
      if (item?.id === id) {
        return item;
      }
      let hit: any;
      item?.children?.forEach((child: any) => {
        hit = hit ?? findQuestion(child, id);
      });
      return hit;
    };
    const twoRoomQuestion = findQuestion(assemblySite, "tworoomq");

    // Same reasoning for the customer's own domain: the switch is answered in
    // the (internal-only) features section, the row it gates lives in the
    // network section, and handing over the question object is what makes the
    // row appear the moment the switch is flipped.
    //
    // The STORED answer has to come along separately, the way bgp and ai do it.
    // The catalog is built first and `factsFromObject` loads the answers into it
    // afterwards, so at construction every fact still reads unanswered - gating
    // on the question object alone hides the row on every open of a
    // questionnaire that has the feature switched on.
    const customDomainQuestion = findQuestion(features, "custom_domain");
    const customDomainRaw = oldValues
      ? oldValues["features.enable.custom_domain"]
      : undefined;
    const customDomainEnabled: boolean =
      customDomainRaw && customDomainRaw != null
        ? (getYesNoAsBoolean(customDomainRaw.value) ?? false)
        : false;

    const network = opsanioNetworks(
      oldValues,
      questionsRights,
      setDefaultValues,
      defaultValuesFallback,
      bgpEnabled,
      twoRoomQuestion,
      customDomainQuestion,
      customDomainEnabled,
    );

    this.addChild(network);

    const auth = opsanioAuth(questionsRights, setDefaultValues);
    this.addChild(auth);

    const scanners = opsanioScanners(questionsRights);
    this.addChild(scanners);

    const retentions = retentionSection(questionsRights, setDefaults);
    this.addChild(retentions);

    // Container-hardening rollout switches (#248). Always attached, so its
    // answers survive a save by someone who cannot see it; shown only with
    // `opsanio/hardening · view` to internal users, editable only with
    // `update`. The questionnaire PUT enforces the same on the server.
    const hardening = hardeningSection();
    this.addChild(hardening.section);
    hardening.section.visible =
      getOrgAttributeFlags(user).isInternal &&
      checkPermission(
        AnketaResourcesEnum.opsanioHardening,
        AnketaActionsEnum.view,
        { isInternal: true },
      );
    const mayEditHardening = checkPermission(
      AnketaResourcesEnum.opsanioHardening,
      AnketaActionsEnum.update,
      { isInternal: true },
    );
    hardening.questions.forEach((q) => {
      q.readonly = !mayEditHardening;
    });

    const hardware = HardwareForm(
      oldValues,
      questionsRights,
      setDefaultValues,
      defaultValuesFallback,
    );
    this.addChild(hardware);
    questionsRights(hardware, {
      isInternal: true,
      isExternal: false,
      hideForExternal: true,
    });
    const comment = commentSection(questionsRights);
    this.addChild(comment);
  }
}
